import { useState, useEffect, useCallback, useRef, memo, useMemo, useContext } from 'react';
import { fetchApi } from 'react-scale/redux/actions';
import { CreateWorkspaceResponse, WorkspaceFileInfo } from '@scale/shared/sphereEngine/types';
import { useAsync } from 'react-use-v13';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import logger from '@scale/frontend-lib/logging';
import _ from 'lodash';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';
import { WorkspaceProps } from 'react-scale/components/scalers/tasks/textcollection/WorkspaceSection';
import { Button, Spinner, Banner, Modal, Tag, HelperText, TextField } from '@scale/scaleui';
import WorkspaceFilesAndOutput from 'react-scale/components/scalers/tasks/textcollection/WorkspaceContainerSection/WorkspaceFilesAndOutput';
import {
  GET_CODING_WORKSPACE_FILES_REFRESH_TIME,
  ASSIGNMENT_DETAILS_FETCH_REFRESH_TIME,
  convertExpirationDatesToTimeLeft,
  convertTimeDifferenceToTimeLeft,
  minutesToMilliseconds,
  maybeStopWorkspace,
} from 'react-scale/components/scalers/tasks/textcollection/WorkspaceContainerSection/helpers';
import uuid from 'uuid';
import {
  WorkspaceContainerAnnotation,
  WorkspaceExecutionData,
} from '@scale/shared/types/params/textcollection';
import { getDefaultAnnotation } from '@scale/shared/textcollection/defaults';
import useGenericCountdownTimer from 'react-scale/components/scalers/tasks/textcollection/WorkspaceContainerSection/useGenericCountdownTimer';
import WorkspaceDisplayModal from 'react-scale/components/scalers/tasks/textcollection/WorkspaceContainerSection/WorkspaceDisplayModal';
import Kbd from 'react-scale/components/design-system/Kbd';
import { Event, logEvent } from 'react-scale/lib/events';
import API from '@scale/frontend-lib/dashboard-api';
import {
  useChatStoreExtract,
  useCurrentStep,
  useStepParams,
} from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore.hooks';
import {
  useChatThemeStore,
  ChatTheme,
} from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useChatThemeStore';
import { useQuery, useMutation } from 'react-query';
import { ReviewLevel } from '@scale/shared-types';
import SAlert from 'react-s-alert';
import save from 'save-file';
import path from 'path';
import { useChatStepChildIdContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatStepChildIdContext';
import { toast } from 'react-toastify';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';

const truncateText = (
  text: string,
  length: number,
): { truncatedText: string; isTruncated: boolean; originalTextLength: number } => {
  return text.length > length
    ? {
        truncatedText:
          text.substring(0, length) + `\n\n...(${text.length - length} CHARACTERS TRUNCATED)...`,
        isTruncated: true,
        originalTextLength: text.length,
      }
    : {
        truncatedText: text,
        isTruncated: false,
        originalTextLength: text.length,
      };
};

interface WorkspaceContainerProps extends WorkspaceProps {
  selectedLanguage?: string;
  changedLanguageCounter: number;
  setToolTipContent: (content: string | null) => void;
}

type WorkspaceConfig = CreateWorkspaceResponse;

enum ScenarioStageType {
  RUN = 'run',
  TEST = 'test',
}

enum ScenarioStreamType {
  STDOUT = 'stdout',
  STDERR = 'stderr',
}

const WORKSPACE_INIT_SCRIPT = `(function(d, s, id){
  SE_BASE = "containers.sphere-engine.com";
  SE_HTTPS = true;
  SE = window.SE || (window.SE = []);
  var js, fjs = d.getElementsByTagName(s)[0];
  if (d.getElementById(id)) return;
  js = d.createElement(s); js.id = id;
  js.src = (SE_HTTPS ? "https" : "http") + "://" + SE_BASE + "/static/sdk/sdk.min.js";
  fjs.parentNode.insertBefore(js, fjs);
}(document, "script", "sphere-engine-jssdk"));
SE.ready = function(f){if (document.readyState != "loading" && document.readyState != "interactive") f();else window.addEventListener("load", f);};
`;
const TIME_TO_WAIT_FOR_WORKSPACE_TO_LOAD = 15 * 1000;
const CODE_FAILED_ERROR_MESSAGE =
  'Please make sure the current code in the workspace executes successfully';
const NO_EXECUTION_ERROR_MESSAGE = 'Please execute the code in the workspace before submitting';
const CLOSE_WORKSPACE_ERROR_MESSAGE = 'Please exit your workspace before submitting';

const FILE_NOT_FOUND_ERROR_MESSAGE = 'File not found';
const FILE_TOO_LARGE_ERROR_MESSAGE = 'Requested file was too large';

// eslint-disable-next-line complexity
function WorkspaceContainerSection({
  field,
  annotation,
  setAnnotation,
  disabled,
  setSubmitError: _setSubmitError,
  assignmentId,
  selectedLanguage, // `selectedLanguage` is deprecated and should never be used.
  changedLanguageCounter,
  setToolTipContent,
}: WorkspaceContainerProps) {
  const reviewLevel = useChatStoreExtract(state => state.reviewLevel);
  const { projectId } = useContext(ChatTaskContext);
  const theme = useChatThemeStore(state => state.theme);
  const currentStep = useCurrentStep();
  const chatStepChildIdContext = useChatStepChildIdContext();
  const stepParams = useStepParams(chatStepChildIdContext.metadata?.stepIndex ?? 0);

  // Use the workspace config from the annotation if it exists
  const [workspaceConfig, setWorkspaceConfig] = useState<WorkspaceConfig | null>(
    annotation.response[0]?.workspaceConfig ?? null,
  );
  const [workspaceIsOpen, setWorkspaceIsOpen] = useState<boolean>(false);
  const [isFetchingWorkspaceConfig, setIsFetchingWorkspaceConfig] = useState<boolean>(false);

  const [isSubscribed, setIsSubscribed] = useState<boolean>(false);
  const isSubscribedRef = useRef(isSubscribed);
  isSubscribedRef.current = isSubscribed;
  const [blockSubmissionOnFailedScenario, setBlockSubmissionOnFailedScenario] =
    useState<boolean>(true);

  const [lastCodeRunEvent, setLastCodeRunEvent] = useState<
    WorkspaceExecutionData | null | undefined
  >(_.first(annotation.response)?.lastCodeRunEvent ?? undefined);
  const [trigger, setTrigger] = useState(true); // SE initialized isn't stateful, so we need to trigger a re-render to check if it's initialized
  const [isLoading, setIsLoading] = useState(false);
  const [filesToExtract, setFilesToExtract] = useState<Partial<WorkspaceFileInfo>[] | null>(
    _.first(annotation.response)?.outputFiles ?? null,
  );
  const [stdoutOutput, setStdoutOutput] = useState<string | null | undefined>(
    annotation.response[0]?.stdoutOutput ?? null,
  );
  const [stderrOutput, setStderrOutput] = useState<string | null | undefined>(
    annotation.response[0]?.stderrOutput ?? null,
  );

  const [testStdoutOutput, setTestStdoutOutput] = useState<string | null | undefined>(
    annotation.response[0]?.testStdoutOutput ?? null,
  );
  const [testStderrOutput, setTestStderrOutput] = useState<string | null | undefined>(
    annotation.response[0]?.testStderrOutput ?? null,
  );

  const [stdoutOutputUrl, setStdoutOutputUrl] = useState<string | null | undefined>(
    annotation.response[0]?.stdoutOutputUrl ?? null,
  );
  const [stderrOutputUrl, setStderrOutputUrl] = useState<string | null | undefined>(
    annotation.response[0]?.stderrOutputUrl ?? null,
  );

  const [testStdoutOutputUrl, setTestStdoutOutputUrl] = useState<string | null | undefined>(
    annotation.response[0]?.testStdoutOutputUrl ?? null,
  );
  const [testStderrOutputUrl, setTestStderrOutputUrl] = useState<string | null | undefined>(
    annotation.response[0]?.testStderrOutputUrl ?? null,
  );

  const [addressWorkspaceErrorModalIsOpen, setAddressWorkspaceErrorModalIsOpen] =
    useState<boolean>(false);

  const [isRecreatingWorkspace, setIsRecreatingWorkspace] = useState<boolean>(false);

  // In chat task, there can be times where the same workspace is rendered across multiple
  // WorkspaceContainerSections (think per-step usage of the same workspace). In this case, we
  // need a way to uniquely identify each workspace instance, so we can ensure that the correct
  // workspace is affected by our handlers.
  const [WORKSPACE_INSTANCE_UNIQUE_ID] = useState<string>(uuid.v4());

  const [shouldMountWorkspaceModal, setShouldMountWorkspaceModal] = useState<boolean>(false);

  // Keeps track of who opened the workspace
  const [workspaceOpenedBy, setWorkspaceOpenedBy] = useState<string[] | null | undefined>(
    annotation.response[0]?.workspaceOpenedBy ?? null,
  );

  // Keep track of who has executed code in the workspace
  const [workspaceExecutedBy, setWorkspaceExecutedBy] = useState<string[] | null | undefined>(
    annotation.response[0]?.workspaceExecutedBy ?? null,
  );

  const [workspaceConfigFetchError, setWorkspaceConfigFetchError] = useState<boolean>(false);

  // Only used if field.workspaceContainerConfig.enableContributorControlledFileFetching is true
  const [filePathToDownload, setFilePathToDownload] = useState<string>('');
  const isDownloadingFileRef = useRef<boolean>(false);

  const { count: afterExecutionWaitTime, setCount: setAfterExecutionWaitTime } =
    useGenericCountdownTimer();

  const { count: workspaceRecreationWaitTime, setCount: setWorkspaceRecreationWaitTime } =
    useGenericCountdownTimer();

  const unleashBlockSubmissionOnFailedScenario = useUnleashFeatureFlag(
    'sphere-engine-block-submission',
    {},
    true,
  );

  const {
    isFetching: loadingTaskIdForLogging,
    data: assignmentDetails,
    refetch: fetchTaskId,
  } = useQuery(
    ['assignmentDetails', assignmentId],
    async () => {
      if (!assignmentId) return;
      try {
        const { taskId, batchId } = await API.genai.getAssignmentDetails({ assignmentId });
        return { taskId, batchId };
      } catch (e) {
        logger.error(`Error fetching assignment details: ${e.message}`);
        return;
      }
    },
    {
      enabled: false,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      retry: false,
      staleTime: ASSIGNMENT_DETAILS_FETCH_REFRESH_TIME,
    },
  );

  const { isFetching: isFetchingFiles, refetch: fetchFileContents } = useQuery(
    [
      'getCodingWorkspaceFiles',
      filesToExtract,
      field.field_id,
      field.workspaceContainerConfig.filesToExtract,
      field.workspaceContainerConfig.allowDownloads,
      assignmentId,
      selectedLanguage,
    ],
    async () => {
      if (
        field.workspaceContainerConfig.filesToExtract ||
        (field.workspaceContainerConfig.allowDownloads && isDownloadingFileRef.current)
      ) {
        const { fileInfos } = await fetchApi<{ fileInfos: WorkspaceFileInfo[] }>(
          `/genai/getCodingWorkspaceFiles`,
          {
            body: {
              workspaceId: workspaceConfig?.workspaceId,
              stepId: stepParams.id,
              fieldId: field.field_id,
              assignmentId,
              selectedLanguage,
              additionalId: chatStepChildIdContext.metadata?.candidateId,
              responseIndex: chatStepChildIdContext.metadata?.stepIndex,
              overrideOptions: isDownloadingFileRef.current
                ? {
                    filePathsToFetch: [filePathToDownload],
                  }
                : undefined,
            },
            maxRetries: 3,
            method: 'POST',
            parseAndHandle: true,
            redirectOnUnauthorized: true,
          },
        );

        // Download file if the user has requested to download a file
        if (isDownloadingFileRef.current) {
          downloadFile(fileInfos[0]);
          return;
        }

        const warningMessages = [];
        for (const fileInfo of fileInfos) {
          if (_.isNil(fileInfo.fileContent)) {
            warningMessages.push(`Unable to fetch contents for file ${fileInfo.filePath}`);
          }
        }

        if (warningMessages.length > 0) {
          toast(warningMessages.join('\n'), {
            type: 'warning',
            autoClose: 2000,
            position: 'top-right',
          });
        }

        if (fileInfos.length > 0) {
          setFilesToExtract(fileInfos);
        }
      }
    },
    {
      enabled: false,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      retry: 3,
      staleTime: GET_CODING_WORKSPACE_FILES_REFRESH_TIME,
    },
  );

  /**
   * We make this query once when the component is rendered to check if any changes were made
   * to the workspace image. If so, we should recreate the workspace.
   */
  const { isFetching: isFetchingStatusInfo, data: workspaceImageVersion } = useQuery(
    ['getCodingWorkspaceStatusInfo', assignmentId, projectId],
    async () => {
      if (!projectId) {
        return undefined;
      }

      const { workspaceImageVersion } = await API.genai.getWorkspaceStatusInfo({
        projectId,
      });
      return workspaceImageVersion;
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      retry: 3,
      staleTime: Infinity,
    },
  );

  const ignoreScenarioErrors = useMemo(
    () =>
      !field.enforceCodeExecutionSuccess ||
      !blockSubmissionOnFailedScenario ||
      (!!selectedLanguage &&
        !field.workspaceContainerConfig?.languageToWorkspaceProjectIdMap?.[selectedLanguage]
          ?.enforceCodeExecutionSuccess),
    [
      blockSubmissionOnFailedScenario,
      field.enforceCodeExecutionSuccess,
      field.workspaceContainerConfig?.languageToWorkspaceProjectIdMap,
      selectedLanguage,
    ],
  );

  const shouldShowFileAndOutputDisplay = useMemo(
    () =>
      !field.enforceCodeExecutionSuccess ||
      !blockSubmissionOnFailedScenario ||
      (lastCodeRunEvent && lastCodeRunEvent.result.status.code === 15),
    [blockSubmissionOnFailedScenario, field.enforceCodeExecutionSuccess, lastCodeRunEvent],
  );

  const refreshWorkspace = useCallback(() => {
    setShouldMountWorkspaceModal(false);
    setWorkspaceIsOpen(false);
    setIsSubscribed(false);
    setTimeout(() => {
      setWorkspaceIsOpen(true);
      setShouldMountWorkspaceModal(true);
    }, 1000);
  }, [setShouldMountWorkspaceModal, setWorkspaceIsOpen, setIsSubscribed]);

  // We need to prevent the user from exiting the IDE before after-execution events have
  // been properly handled by the workspace. This is because the workspace will send
  // after-execution events to the parent window, and if the user exits the IDE before
  // these events are handled, the parent window will not be able to handle the events.
  const afterScenarioExecutionHandler = useCallback(() => {
    setAfterExecutionWaitTime(3);
  }, [setAfterExecutionWaitTime]);

  const setAfterWorkspaceRecreationTimer = useCallback(() => {
    setWorkspaceRecreationWaitTime(70);
  }, [setWorkspaceRecreationWaitTime]);

  /**
   * Fetches workspace credentials from the server
   * If `discardExistingWorkspace` is true, the server will discard any existing workspace and return a new one instead
   */
  const { mutateAsync: fetchWorkspaceConfig } = useMutation({
    mutationFn: async ({ forceRecreateWorkspace }: { forceRecreateWorkspace?: boolean }) => {
      setIsFetchingWorkspaceConfig(true);
      setWorkspaceConfigFetchError(false);
      const fetchedWorkspaceConfig = await fetchApi<WorkspaceConfig>(
        `/genai/createCodingWorkspace`,
        {
          body: {
            fieldId: field.field_id,
            assignmentId,
            selectedLanguage,
            forceRecreateWorkspace,
            // `additionalId` should only be defined for TC Per Response steps, and is used to identify the specific candidate.
            additionalId: chatStepChildIdContext.metadata?.candidateId,
            responseIndex: chatStepChildIdContext.metadata?.stepIndex,
            stepId: stepParams.id,
          },
          maxRetries: 3,
          method: 'POST',
          parseAndHandle: true,
          redirectOnUnauthorized: true,
        },
      );
      setWorkspaceConfig(() => fetchedWorkspaceConfig);

      if (fetchedWorkspaceConfig) {
        setToolTipContent(`Workspace expires after ${convertExpirationDatesToTimeLeft(
          fetchedWorkspaceConfig.expiresAt,
        )}
      or after ${convertTimeDifferenceToTimeLeft(
        minutesToMilliseconds(fetchedWorkspaceConfig.inactivityFreezeTime),
      )} of inactivity`);
      }
    },
    onSettled: () => setIsFetchingWorkspaceConfig(false),
    onError: () => {
      setWorkspaceConfigFetchError(true);
      throw new Error('Error fetching workspace config');
    },
  });

  const handleWorkspaceRecreation = useCallback(async () => {
    const oldWorkspaceConfig = workspaceConfig;
    try {
      setIsRecreatingWorkspace(true);
      setWorkspaceIsOpen(false);
      setShouldMountWorkspaceModal(false);
      setIsSubscribed(false);
      await fetchWorkspaceConfig({ forceRecreateWorkspace: true });
      setFilesToExtract(null);
      setStdoutOutput(null);
      setStderrOutput(null);
      setTestStdoutOutput(null);
      setTestStderrOutput(null);
      setStdoutOutputUrl(null);
      setStderrOutputUrl(null);
      setTestStdoutOutputUrl(null);
      setTestStderrOutputUrl(null);
      setLastCodeRunEvent(null);
      setAfterWorkspaceRecreationTimer();
      setAddressWorkspaceErrorModalIsOpen(false);
    } catch {
      setWorkspaceConfig(() => oldWorkspaceConfig);
    } finally {
      setIsRecreatingWorkspace(false);
    }
  }, [
    workspaceConfig,
    setFilesToExtract,
    setStdoutOutput,
    setStderrOutput,
    setTestStdoutOutput,
    setTestStderrOutput,
    setStdoutOutputUrl,
    setStderrOutputUrl,
    setTestStdoutOutputUrl,
    setTestStderrOutputUrl,
    setLastCodeRunEvent,
    setWorkspaceConfig,
    fetchWorkspaceConfig,
    setAddressWorkspaceErrorModalIsOpen,
    setAfterWorkspaceRecreationTimer,
    setWorkspaceIsOpen,
    setShouldMountWorkspaceModal,
    setIsSubscribed,
  ]);

  const downloadFile = useCallback(
    async (file: WorkspaceFileInfo) => {
      if (!filePathToDownload) {
        return;
      }

      if (file.fileContent == null) {
        SAlert.error(
          `Failed to download file "${file.filePath}". Please check to ensure you input your file path correctly and that your workspace is running, and try again.`,
        );
        return;
      }

      await save(new Blob([file.fileContent]), path.basename(filePathToDownload));
    },
    [filePathToDownload],
  );

  // This trigger interval is used to check if the workspace has been initialized so we can set up event listenrs
  // This interval will only be triggered if the workspace is open and we haven't subscribed to the events yet
  useEffect(() => {
    if (isSubscribed || !shouldMountWorkspaceModal) {
      return;
    }
    const intervalId = setInterval(() => {
      setTrigger(prev => !prev);
    }, 500);

    return () => clearInterval(intervalId);
  }, [trigger, isSubscribed, shouldMountWorkspaceModal]);

  useEffect(() => {
    !_.isNil(unleashBlockSubmissionOnFailedScenario) &&
      setBlockSubmissionOnFailedScenario(unleashBlockSubmissionOnFailedScenario);
  }, [unleashBlockSubmissionOnFailedScenario]);

  useEffect(() => {
    if (workspaceIsOpen) {
      setIsLoading(true);

      // Set a timer to remove loading after 5 seconds
      const timer = setTimeout(() => {
        setIsLoading(false);
        if (isSubscribedRef.current) {
          return;
        }
        logger.warn('Workspace took too long to load', {
          workspaceId: workspaceConfig?.workspaceId,
        });
        setBlockSubmissionOnFailedScenario(false);
      }, TIME_TO_WAIT_FOR_WORKSPACE_TO_LOAD);

      return () => clearTimeout(timer); // Clear the timer if component is unmounted
    }
  }, [workspaceIsOpen]);

  const initializeSDK = useCallback(() => {
    // https://docs.sphere-engine.com/containers/workspace/quickstart
    const SCRIPT_ID = 'sphere-engine-workspace-initialization-script';
    const existingScript = document.getElementById(SCRIPT_ID);
    if (existingScript && document.body.contains(existingScript)) {
      // Initialize the SDK only once globally
      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;

    script.innerHTML = WORKSPACE_INIT_SCRIPT;
    document.body.appendChild(script);
  }, []);

  // *** START workspace initialization code ***
  useEffect(() => {
    fetchTaskId();
    initializeSDK();
    // SDK should only ever get initialized once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setSubmitError = useCallback(
    (error: string) => {
      _setSubmitError && _setSubmitError(field.field_id, error);
    },
    [_setSubmitError, field.field_id],
  );

  const checkAndSetSubmitError = useCallback(() => {
    if (shouldMountWorkspaceModal) {
      setSubmitError(CLOSE_WORKSPACE_ERROR_MESSAGE);
      return;
    }

    if (
      field.enforceCodeRan &&
      reviewLevel === ReviewLevel.Attempt &&
      lastCodeRunEvent?.assignmentId !== assignmentId
    ) {
      setSubmitError(NO_EXECUTION_ERROR_MESSAGE);
      return;
    }

    const cachedLastCodeRunEvent = _.first(annotation.response)?.lastCodeRunEvent;

    // If the lastCodeRunEvent is the same as the cachedLastCodeRunEvent, we don't need to re-fetch files
    if (
      lastCodeRunEvent &&
      cachedLastCodeRunEvent &&
      lastCodeRunEvent?.id === cachedLastCodeRunEvent?.id
    ) {
      // Allow submission unless compilation checking (scenario errors) is enabled
      // and the last code run event failed
      if (ignoreScenarioErrors || lastCodeRunEvent?.result?.status?.code === 15) {
        setSubmitError('');
      } else {
        setSubmitError(CODE_FAILED_ERROR_MESSAGE);
      }
      return;
    }

    // Only fetch files if scenario errors are ignored or the last code run event succeeded.
    // If we care about scenario errors, then we only want to fetch code that run successfully.
    if (ignoreScenarioErrors) {
      setSubmitError('');
    } else if (!lastCodeRunEvent) {
      // Scenario Errors Enabled: Haven't run the code yet
      setSubmitError(NO_EXECUTION_ERROR_MESSAGE);
    } else if (lastCodeRunEvent?.result?.status?.code !== 15) {
      // Scenario Errors Enabled: Code failed to run
      setSubmitError(CODE_FAILED_ERROR_MESSAGE);
    } else {
      setSubmitError('');
    }
  }, [
    annotation.response,
    assignmentId,
    field.enforceCodeRan,
    ignoreScenarioErrors,
    lastCodeRunEvent,
    reviewLevel,
    setSubmitError,
    shouldMountWorkspaceModal,
  ]);

  // Code Execution check performed in this useEffect
  useEffect(() => {
    // Workspace must be closed (i.e demounted) before submitting
    checkAndSetSubmitError();

    // Only re-fetch files if the workspace opens/close OR the user executes code in the workspace
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastCodeRunEvent, shouldMountWorkspaceModal]);

  const resetOutputs = useCallback(() => {
    setStdoutOutput(() => '');
    setStderrOutput(() => '');
    setTestStdoutOutput(() => '');
    setTestStderrOutput(() => '');
    setStdoutOutputUrl(() => '');
    setStderrOutputUrl(() => '');
    setTestStdoutOutputUrl(() => '');
    setTestStderrOutputUrl(() => '');
  }, []);

  const afterScenarioExecutionExtHandler = useCallback(
    async (e: any) => {
      const SE = (window as any).SE;
      if (!SE?.workspace(WORKSPACE_INSTANCE_UNIQUE_ID)) {
        return;
      }

      resetOutputs();
      const { scenario, result } = e.data; // also we get back the result of each stage, but excluding for now since no new information
      logger.info('Sphere Engine code run event', {
        scenario,
        result,
      });

      const workspace = SE.workspace(WORKSPACE_INSTANCE_UNIQUE_ID);

      // Extract the run stage STDOUT and STDERR outputs
      workspace.getStageStream('run', 'output');
      workspace.getStageStream('run', 'error');

      // Extract the test stage STDOUT and STDERR outputs
      workspace.getStageStream('test', 'output');
      workspace.getStageStream('test', 'error');

      if (ignoreScenarioErrors || result?.status?.code === 15) {
        fetchFileContents();
      }

      setLastCodeRunEvent({ scenario, result, id: uuid.v4(), assignmentId });
      setWorkspaceExecutedBy(prev => {
        if (!workspaceConfig || !workspaceConfig.userId) {
          return prev;
        }

        if (!prev) {
          return [workspaceConfig.userId];
        }

        return prev.includes(workspaceConfig.userId) ? prev : [...prev, workspaceConfig.userId];
      });

      const executionEvent = {
        projectId,
        worker: workspaceConfig?.userId,
        assignmentId,
        workspaceId: workspaceConfig?.workspaceId,
        fieldId: field.field_id,
        stepId: currentStep?.id,
        stepType: currentStep?.type,
        taskId: assignmentDetails?.taskId,
        batchId: assignmentDetails?.batchId,
        reviewLevel,
        timestamp: new Date(),
        successfulExecution: result?.status?.code === 15,
        statusCode: result?.status?.code,
        workspaceContainerConfig: field.workspaceContainerConfig,
        enforceCodeExecutionSuccess: field.enforceCodeExecutionSuccess,
        enforceCodeRan: field.enforceCodeRan,
      };
      logEvent(Event.SphereWorkspaceExecutionEvent, executionEvent);
    },
    [
      WORKSPACE_INSTANCE_UNIQUE_ID,
      resetOutputs,
      ignoreScenarioErrors,
      assignmentId,
      projectId,
      workspaceConfig,
      field.field_id,
      field.workspaceContainerConfig,
      field.enforceCodeExecutionSuccess,
      field.enforceCodeRan,
      currentStep?.id,
      currentStep?.type,
      assignmentDetails?.taskId,
      assignmentDetails?.batchId,
      reviewLevel,
      fetchFileContents,
    ],
  );

  const setOutputMap = useMemo(
    () => ({
      [ScenarioStageType.RUN]: {
        [ScenarioStreamType.STDOUT]: setStdoutOutput,
        [ScenarioStreamType.STDERR]: setStderrOutput,
      },
      [ScenarioStageType.TEST]: {
        [ScenarioStreamType.STDOUT]: setTestStdoutOutput,
        [ScenarioStreamType.STDERR]: setTestStderrOutput,
      },
    }),
    [],
  );

  const setOutputUrlMap = useMemo(
    () => ({
      [ScenarioStageType.RUN]: {
        [ScenarioStreamType.STDOUT]: setStdoutOutputUrl,
        [ScenarioStreamType.STDERR]: setStderrOutputUrl,
      },
      [ScenarioStageType.TEST]: {
        [ScenarioStreamType.STDOUT]: setTestStdoutOutputUrl,
        [ScenarioStreamType.STDERR]: setTestStderrOutputUrl,
      },
    }),
    [],
  );

  const { mutateAsync: uploadOutputToCDS, isLoading: isUploadingToCDS } = useMutation(
    async ({
      output,
      assignmentId,
      stage,
      streamName,
    }: {
      output: string;
      assignmentId: string;
      stage: string;
      streamName: string;
    }) => {
      const { cdsUrl } = await fetchApi<{ cdsUrl: string }>(`/genai/uploadOutputToCDS`, {
        body: {
          output,
          assignmentId,
        },
        maxRetries: 3,
        method: 'POST',
        parseAndHandle: true,
        redirectOnUnauthorized: true,
      });
      setOutputUrlMap[stage as ScenarioStageType][streamName as ScenarioStreamType](() => cdsUrl);
    },
    {
      onError: (error: Error) => {
        toast(`Unable to save output - ${error?.message}.`, {
          type: 'error',
          autoClose: 2000,
          position: 'top-right',
        });
      },
    },
  );

  const stageStreamOutputHandler = useCallback(
    async (e: any) => {
      const { data } = e;
      // console.log('IN STAGE STREAM OUTPUT HANDLER', data);
      if (!_.isNil(data.error)) {
        setOutputMap[data.stage as ScenarioStageType][data.stream_name as ScenarioStreamType](
          () => '',
        );
        setOutputUrlMap[data.stage as ScenarioStageType][data.stream_name as ScenarioStreamType](
          () => '',
        );

        if (data.error === FILE_TOO_LARGE_ERROR_MESSAGE) {
          toast(`Output too large (> 16 MB), unable to save output.`, {
            type: 'error',
            autoClose: 2000,
            position: 'top-right',
          });
        } else if (data.error !== FILE_NOT_FOUND_ERROR_MESSAGE) {
          toast(`Unable to save output - ${data.error}.`, {
            type: 'error',
            autoClose: 2000,
            position: 'top-right',
          });
        }
      } else {
        const outputContent = Buffer.from(data.content, 'base64').toString();
        if (data.content && assignmentId) {
          await uploadOutputToCDS({
            output: outputContent,
            assignmentId,
            stage: data.stage,
            streamName: data.stream_name,
          });
        }
        setOutputMap[data.stage as ScenarioStageType][data.stream_name as ScenarioStreamType](
          () => truncateText(outputContent, 15000).truncatedText,
        );
      }
    },
    [assignmentId, setOutputMap, setOutputUrlMap, uploadOutputToCDS],
  );

  const subscribeToEvents = useCallback(() => {
    const SE = (window as any).SE;
    if (!SE?.workspace?.(WORKSPACE_INSTANCE_UNIQUE_ID)) {
      logger.warn('Workspace not found, cannot subscribe from events');
      return;
    }

    const workspace = SE.workspace(WORKSPACE_INSTANCE_UNIQUE_ID);
    workspace.events.subscribe('afterScenarioExecution', afterScenarioExecutionHandler);
    workspace.events.subscribe('afterScenarioExecutionExt', afterScenarioExecutionExtHandler);
    workspace.events.subscribe('stageStream', stageStreamOutputHandler);
    setIsSubscribed(true);
    logger.info('Subscribed to workspace events');
  }, [
    WORKSPACE_INSTANCE_UNIQUE_ID,
    afterScenarioExecutionHandler,
    afterScenarioExecutionExtHandler,
    stageStreamOutputHandler,
  ]);

  const unsubscribeFromEvents = useCallback(() => {
    const SE = (window as any).SE;
    if (!SE?.workspace?.(WORKSPACE_INSTANCE_UNIQUE_ID)) {
      logger.warn('Workspace not found, cannot unsubscribe from events');
      return;
    }

    const workspace = SE.workspace(WORKSPACE_INSTANCE_UNIQUE_ID);
    workspace.events.unsubscribe('afterScenarioExecution', afterScenarioExecutionHandler);
    workspace.events.unsubscribe('afterScenarioExecutionExt', afterScenarioExecutionExtHandler);
    workspace.events.unsubscribe('stageStream', stageStreamOutputHandler);
    setIsSubscribed(false);
    logger.info('Unsubscribed from workspace events');
  }, [
    WORKSPACE_INSTANCE_UNIQUE_ID,
    afterScenarioExecutionHandler,
    afterScenarioExecutionExtHandler,
    stageStreamOutputHandler,
  ]);

  const destroyWorkspace = useCallback(() => {
    const SE = (window as any).SE;
    if (!SE?.workspace?.(WORKSPACE_INSTANCE_UNIQUE_ID)) {
      logger.warn('Workspace not found, cannot destroy workspace');
      return;
    }

    SE.workspace(WORKSPACE_INSTANCE_UNIQUE_ID).destroy();
  }, [WORKSPACE_INSTANCE_UNIQUE_ID]);

  const handleWorkspaceDemount = useCallback(async () => {
    try {
      unsubscribeFromEvents();
      destroyWorkspace();
    } catch (e) {
      logger.error('Error destroying workspace', e);
    }

    setShouldMountWorkspaceModal(false);
    setWorkspaceIsOpen(false);

    // If scenario errors should be ignored, we want to fetch file contents immediately
    // after closing the workspace
    if (ignoreScenarioErrors) {
      await fetchFileContents();
    }

    // We need to await `fetchFileContents` above because we won't be able to fetch files
    // from a stopped workspace. In the case where downloads are allowed, we shouldn't stop
    // the workspace immediately since downloading files from a workspace requires that workspace
    // to be running.
    if (workspaceConfig?.workspaceId && !field.workspaceContainerConfig.allowDownloads) {
      maybeStopWorkspace(workspaceConfig?.workspaceId);
    }
  }, [
    ignoreScenarioErrors,
    workspaceConfig?.workspaceId,
    field.workspaceContainerConfig.allowDownloads,
    unsubscribeFromEvents,
    fetchFileContents,
    destroyWorkspace,
  ]);

  const handleWorkspaceOpen = useCallback(() => {
    try {
      // In case there are lingering listeners or the previous workspace hasn't been destroyed, we should
      // unsubscribe from events and destroy the workspace before mounting a new one.
      if (!shouldMountWorkspaceModal) {
        unsubscribeFromEvents();
        destroyWorkspace();
      }
    } catch {
      // ignored
    }

    setWorkspaceIsOpen(true);
    setShouldMountWorkspaceModal(true);
    setWorkspaceOpenedBy(prev => {
      if (!workspaceConfig || !workspaceConfig.userId) {
        return prev;
      }

      if (!prev) {
        return [workspaceConfig.userId];
      }

      return prev.includes(workspaceConfig.userId) ? prev : [...prev, workspaceConfig.userId];
    });

    const openEvent = {
      projectId,
      worker: workspaceConfig?.userId,
      assignmentId,
      workspaceId: workspaceConfig?.workspaceId,
      fieldId: field.field_id,
      stepId: currentStep?.id,
      stepType: currentStep?.type,
      taskId: assignmentDetails?.taskId,
      batchId: assignmentDetails?.batchId,
      reviewLevel,
      timestamp: new Date(),
      workspaceContainerConfig: field.workspaceContainerConfig,
      enforceCodeExecutionSuccess: field.enforceCodeExecutionSuccess,
      enforceCodeRan: field.enforceCodeRan,
    };
    logEvent(Event.SphereWorkspaceOpenEvent, openEvent);
  }, [
    shouldMountWorkspaceModal,
    projectId,
    workspaceConfig,
    assignmentId,
    field.field_id,
    field.workspaceContainerConfig,
    field.enforceCodeExecutionSuccess,
    field.enforceCodeRan,
    currentStep?.id,
    currentStep?.type,
    assignmentDetails?.taskId,
    assignmentDetails?.batchId,
    reviewLevel,
    unsubscribeFromEvents,
    destroyWorkspace,
  ]);

  useEffect(() => {
    const SE = (window as any).SE;
    if (
      isSubscribed ||
      !workspaceConfig ||
      !SE?.workspace?.(WORKSPACE_INSTANCE_UNIQUE_ID) ||
      !shouldMountWorkspaceModal
    ) {
      return;
    }

    if (!SE?.initialized) {
      return;
    }

    try {
      subscribeToEvents();
    } catch (e) {
      logger.error('Error subscribing to workspace events', e);
    }
    // This trigger interval is used to check if the workspace has been initialized so we can set up event listeners
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger]);

  useEffect(() => {
    if (changedLanguageCounter > 0) {
      setLastCodeRunEvent(undefined);
    }
  }, [changedLanguageCounter]);

  const isDefaultAnnotation = useMemo(() => {
    return _.isEqual(annotation, getDefaultAnnotation(field));
  }, [annotation, field]);

  useAsync(async () => {
    /**
     * If the workspace config is already present in the annotation, we don't fetch is again from the server.
     * We use this stored workspace config directly.
     */
    if (workspaceConfig) {
      return;
    }

    /**
     * If the workspace config is not present in the annotation, we need to fetch it from the server.
     * There are two cases here:
     *
     * 1. There is no previous work done on this field, so there is only the default annotation.
     * 2. Previous work has been done on this field, so there is a response annotation, but the workspace config is not present.
     *
     * In the first case, we should create a workspace from scratch. The `forceRecreateWorkspace` flag is set to `true`
     * to indicate that we force removal of an existing workspace if there is one (there shouldn't be one, but just in case)
     * and create a new workspace from scratch with a new config.
     *
     * Because we used to store workspaces in the task metadata, the if the annotation for this workspace container field is defined
     * but the workspaceConfig field is not present in the annotation, the workspace may be cached in the task metadata.
     * In this case, we should set `forceRecreateWorkspace` to `false`, which will signal to the server to check the task metadata
     * for a cached workspace and use that if it exists, before attempting to create a new workspace.
     */
    if (isDefaultAnnotation) {
      await fetchWorkspaceConfig({ forceRecreateWorkspace: true });
    } else {
      await fetchWorkspaceConfig({ forceRecreateWorkspace: false });
    }
  }, [workspaceConfig, isDefaultAnnotation]);

  useAsync(async () => {
    /**
     * If the workspace image version has changed, we need to recreate the workspace.
     */
    if (
      workspaceConfig &&
      workspaceImageVersion !== undefined &&
      workspaceImageVersion !== workspaceConfig.workspaceImageVersion &&
      !isRecreatingWorkspace &&
      !isFetchingWorkspaceConfig
    ) {
      toast.warning(
        'Your workspace has been re-created due to an update. Please contact support for more details.',
        {
          autoClose: 10000,
          position: 'top-right',
        },
      );
      await handleWorkspaceRecreation();
      return;
    }
  }, [workspaceConfig, workspaceImageVersion, isRecreatingWorkspace, isFetchingWorkspaceConfig]);

  // Update annotation after any state update
  useEffect(() => {
    if (!workspaceConfig) {
      return;
    }

    const newAnnotation: WorkspaceContainerAnnotation = {
      selectedLanguage,
      workspaceConfig,
      workspaceId: workspaceConfig.workspaceId,
      lastCodeRunEvent: lastCodeRunEvent ?? null,
      outputFiles: filesToExtract,
      stdoutOutput,
      stderrOutput,
      testStdoutOutput,
      testStderrOutput,
      stdoutOutputUrl,
      stderrOutputUrl,
      testStdoutOutputUrl,
      testStderrOutputUrl,
      workspaceOpenedBy: workspaceOpenedBy ?? [],
      workspaceExecutedBy: workspaceExecutedBy ?? [],
    };

    const annotationResponse = annotation.response[0];
    // If the fields we need to update in the annotation haven't changed,
    // we should not update the annotation
    if (_.isEqual(annotationResponse, newAnnotation)) {
      return;
    }

    setAnnotation({
      ...annotation,
      field_id: field.field_id,
      response: [newAnnotation],
    });
    // Explicitly ignore `annotation` as a dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    field.field_id,
    setAnnotation,
    filesToExtract,
    stdoutOutput,
    stderrOutput,
    testStdoutOutput,
    testStderrOutput,
    stdoutOutputUrl,
    stderrOutputUrl,
    testStdoutOutputUrl,
    testStderrOutputUrl,
    workspaceOpenedBy,
    workspaceExecutedBy,
    lastCodeRunEvent,
    workspaceConfig,
    selectedLanguage,
  ]);

  useEffect(() => {
    return () => {
      setSubmitError('');
      destroyWorkspace();
    };
  }, [destroyWorkspace, setSubmitError]);

  // If we're fetching files or uploading outputs to CDS, we should not allow CBs to submit until it's finished, or
  // they may submit the step before these files or outputs are set on the annotation object. However, we don't want to
  // block forever in case there are issues with fetching files or uploading outputs, so we'll set a timeout to
  // unblock CBs from submitting once 30 seconds have passed.
  useEffect(() => {
    let errorTimeout: NodeJS.Timeout | undefined = undefined;

    if (isFetchingFiles || isUploadingToCDS) {
      // Start a 30-second timeout to set the submission error
      errorTimeout = setTimeout(() => {
        checkAndSetSubmitError();
      }, 15000);
      setSubmitError('Please wait while we fetch the latest files and outputs...');
    }

    // Clear the error and timeout when both operations are complete
    if (!isFetchingFiles && !isUploadingToCDS) {
      if (errorTimeout) {
        clearTimeout(errorTimeout);
      }
      checkAndSetSubmitError();
    }

    // Cleanup function to clear timeout on unmount or dependency change
    return () => clearTimeout(errorTimeout);

    // We only want to trigger the useEffect when fetching files or uploading to CDS
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetchingFiles, isUploadingToCDS]);

  // Opening the workspace will be disabled if there isn't defined workspaceConfig fetched, if disabled is true,
  // or a force enable workspace access override (used primarily to allow opening the workspace in L4),
  const disableWorkspace = useMemo(
    () =>
      ((!workspaceConfig || disabled) && !workspaceConfig?.forceEnableWorkspaceAccess) ||
      loadingTaskIdForLogging ||
      isFetchingStatusInfo ||
      isFetchingWorkspaceConfig,
    [
      disabled,
      loadingTaskIdForLogging,
      workspaceConfig,
      isFetchingStatusInfo,
      isFetchingWorkspaceConfig,
    ],
  );

  return (
    <div className="mt-2 flex flex-col gap-2 mb-2">
      <div className="mb-2">
        <HelperText>
          <span
            className="underline cursor-pointer"
            onClick={() => setAddressWorkspaceErrorModalIsOpen(true)}
          >
            {' '}
            Recreate Your Workspace{' '}
          </span>
        </HelperText>
      </div>
      {workspaceConfig && shouldMountWorkspaceModal && (
        <WorkspaceDisplayModal
          workspaceIsOpen={workspaceIsOpen}
          workspaceInstanceId={WORKSPACE_INSTANCE_UNIQUE_ID}
          workspaceConfig={workspaceConfig}
          handleWorkspaceDemount={handleWorkspaceDemount}
          handleRefreshWorkspace={refreshWorkspace}
          afterExecutionWaitTime={afterExecutionWaitTime}
          blockSubmissionOnFailedScenario={blockSubmissionOnFailedScenario}
          isLoading={isLoading}
          isSubscribed={isSubscribed}
          setWorkspaceIsOpen={setWorkspaceIsOpen}
          isFetchingFiles={(isFetchingFiles && !isDownloadingFileRef.current) || isUploadingToCDS}
        />
      )}
      <Modal
        open={addressWorkspaceErrorModalIsOpen}
        onClose={() => setAddressWorkspaceErrorModalIsOpen(false)}
        title="Recreate Your Workspace"
        className={theme === ChatTheme.Dark ? 'federal-theme' : ''}
        actions={
          <div className="flex items-center gap-2 justify-end">
            <Button
              className="scale-theme flex gap-1 items-center ml-1"
              onClick={() => setAddressWorkspaceErrorModalIsOpen(false)}
              size="sm"
              variant="neutral"
            >
              Cancel
            </Button>
            {!isRecreatingWorkspace ? (
              <Button
                className="scale-theme flex gap-1 items-center ml-1"
                onClick={handleWorkspaceRecreation}
                size="sm"
                variant="primary"
                disabled={disabled || workspaceRecreationWaitTime > 0}
              >
                <FontAwesomeIcon icon={'redo'}></FontAwesomeIcon>
                Recreate Workspace IDE{' '}
                {workspaceRecreationWaitTime > 0 ? `(${workspaceRecreationWaitTime})` : ''}
              </Button>
            ) : (
              <Spinner className="" size="sm" variant="primary" />
            )}
          </div>
        }
      >
        <div className="w-[600px]">
          <div className="flex items-center ml-1 pb-3">
            Has your workspace expired? Or are you experiencing any of the following issues when
            opening your workspace?
          </div>

          <div className="flex gap-2 items-center ml-1">
            <FontAwesomeIcon icon={'caret-right'} />
            <span> Workspace is no longer available </span>
          </div>
          <div className="flex gap-2 items-center ml-1">
            <FontAwesomeIcon icon={'caret-right'} />
            <span> Resuming the workspace is not possible due to image corruption </span>
          </div>
          <div className="flex gap-2 items-center ml-1">
            <FontAwesomeIcon icon={'caret-right'} />
            <span> An internal error has occurred </span>
          </div>

          <div className="flex items-center ml-1 pt-3">
            If so, please click the button below to recreate your workspace. Recreating the
            workspace will discard all changes made within the IDE, so please only do so if you are
            experiencing one of the above issues.
          </div>
        </div>
      </Modal>

      <div className="bg-neutral-100 p-9 py-14 rounded-md flex justify-center items-center relative">
        {workspaceConfig ? (
          <>
            {lastCodeRunEvent && (
              <Tag
                className="absolute top-3 right-2 secondary"
                variant={lastCodeRunEvent?.result.status.code === 15 ? 'secondary' : 'warning'}
                icon={lastCodeRunEvent?.result.status.code === 15 ? 'check' : 'exclamation'}
                size="sm"
              >
                {lastCodeRunEvent?.result.status.code === 15
                  ? 'Executed successfully'
                  : 'Executed with errors'}
              </Tag>
            )}
            <Button
              className="flex gap-1 items-center ml-1 transition-none"
              onClick={handleWorkspaceOpen}
              size="sm"
              variant="secondary"
              disabled={disableWorkspace}
            >
              {shouldMountWorkspaceModal ? (
                <>
                  <Spinner className="" size="xs" variant="primary" />
                  Show Workspace IDE{' '}
                  <span>
                    <Kbd>CTRL/CMD</Kbd> + <Kbd>M</Kbd>
                  </span>
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={'code'} className="text-gray-500"></FontAwesomeIcon>
                  <div className="flex flex-row text-gray-500 items-center">
                    Open Workspace IDE{' '}
                  </div>
                  <span>
                    <Kbd>CTRL/CMD</Kbd> + <Kbd>M</Kbd>
                  </span>
                </>
              )}
            </Button>
            {shouldMountWorkspaceModal && (
              <Button
                className="flex gap-1 items-center ml-1 pl-1 p-2 transition-none"
                onClick={handleWorkspaceDemount}
                size="sm"
                variant="white"
                disabled={afterExecutionWaitTime > 0}
              >
                <FontAwesomeIcon icon={'window-close'}></FontAwesomeIcon>
                Exit Workspace {afterExecutionWaitTime > 0 ? ` (${afterExecutionWaitTime})` : ''}
              </Button>
            )}
          </>
        ) : (
          <Button disabled={true}>
            {workspaceConfigFetchError ? 'IDE Unavailable' : 'Loading IDE...'}
          </Button>
        )}
      </div>

      {field.workspaceContainerConfig.allowDownloads && (
        <div>
          <TextField
            helperText="Enter the file path of the file you want to download from the IDE relative to `/home/user/workspace`. For example, if you want to download `/home/user/workspace/file.txt`, input `file.text`. Ensure the file you wish to download exists and that your workspace is running (or was last running no more than 5 minutes ago) before attempting to download the file."
            label=" "
            id="input-file-path"
            type="text"
            placeholder="Input the file path you want to download"
            onChange={e => setFilePathToDownload(e.target.value)}
            value={filePathToDownload}
          />
          <Button
            fullWidth
            onClick={async () => {
              isDownloadingFileRef.current = true;
              await fetchFileContents();
              isDownloadingFileRef.current = false;
            }}
            className="mt-4"
            variant="primary"
            size="lg"
            disabled={isFetchingFiles && isDownloadingFileRef.current}
          >
            Download File{' '}
            {isFetchingFiles && isDownloadingFileRef.current && (
              <Spinner className="ml-2" size="sm" variant="primary" />
            )}
          </Button>
        </div>
      )}

      {/* Only display these one of these two components if there's been executed for this task in the past or if we ignore executions  */}
      {(ignoreScenarioErrors || lastCodeRunEvent) &&
        (shouldShowFileAndOutputDisplay ? (
          <WorkspaceFilesAndOutput
            field={field}
            isFetchingFiles={(isFetchingFiles && !isDownloadingFileRef.current) || isUploadingToCDS}
            filesToDisplay={filesToExtract}
            stdoutOutput={stdoutOutput}
            stderrOutput={stderrOutput}
            testStdoutOutput={testStdoutOutput}
            testStderrOutput={testStderrOutput}
            stdoutOutputUrl={stdoutOutputUrl}
            stderrOutputUrl={stderrOutputUrl}
            testStdoutOutputUrl={testStdoutOutputUrl}
            testStderrOutputUrl={testStderrOutputUrl}
            shouldMountWorkspaceModal={shouldMountWorkspaceModal}
            handleWorkspaceOpen={handleWorkspaceOpen}
            afterExecutionWaitTime={afterExecutionWaitTime}
            disabled={disableWorkspace}
          />
        ) : (
          <Banner
            background="white"
            backgroundVariant="white"
            buttonVariant="neutral"
            title="Your most recent code execution failed - please correct and rerun your code!"
            variant="danger"
          />
        ))}
    </div>
  );
}

export default memo(WorkspaceContainerSection);
